import { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Divider, FormControl, FormHelperText,
  FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch, Typography,
} from '@mui/material';
import { AddOutlined } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import ScheduleService from '../../../shared/services/schedule/schedule.service';
import ThirdPartyConnectionDetailsService from '../../../modules/third-party-connection/shared-services/third-party-connection-details/third-party-connection.service';
import { IThirdPartyConnectionDetailsCompany } from '../../../modules/third-party-connection/shared-services/third-party-connection-details/third-party-connection-details.model';
import { AccountingSyncProvider, CloudSyncProvider, ISchedule } from '../../../shared/services/schedule/schedule.model';
import { ScheduleDialog } from './schedule-dialog';
import { CLOUD_PROVIDER_COPY, isCloudEmissionsProvider } from '../../../modules/third-party-connection/shared-services/cloud-emissions/cloud-emissions.model';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Provider code, e.g. 'xero', or a cloud carbon connector code. */
  provider: AccountingSyncProvider | CloudSyncProvider;
  /** Display name, e.g. 'Xero'. */
  providerName?: string;
  /**
   * sync_accounting (default) syncs one ledger company. sync_cloud_emissions syncs a cloud
   * carbon connector: there is no company to choose, and the run can book the months that
   * need no decision.
   */
  actionType?: 'sync_accounting' | 'sync_cloud_emissions';
}

export const ScheduleSyncDialog = ({ open, onClose, provider, providerName, actionType = 'sync_accounting' }: Props) => {
  const isCloud = actionType === 'sync_cloud_emissions';
  const scheduleService = new ScheduleService();
  const thirdPartyConnectionDetailsService = new ThirdPartyConnectionDetailsService();

  const userNotificationsContext = useContext(UserNotificationsContext);
  const { userDetails } = useContext(UserDetailsContext);
  const isAdmin = userDetails?.roles?.includes('admin') ?? false;

  const [companies, setCompanies] = useState<IThirdPartyConnectionDetailsCompany[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [scheduleId, setScheduleId] = useState('');
  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [openCreateSchedule, setOpenCreateSchedule] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autoCommit, setAutoCommit] = useState(false);

  const displayName = providerName ?? `${provider.charAt(0).toUpperCase()}${provider.slice(1)}`;
  const selectedCompanyName = companies.find(c => c.companyId === companyId)?.companyName;

  // A schedule already syncs this provider+company if it has a matching action.
  const scheduleHasThisSync = (s: ISchedule) => isCloud
    ? (s.scheduleActions || []).some(a =>
      a.actionType === 'sync_cloud_emissions' && (a.actionPayload as any)?.provider === provider)
    : !!companyId && (s.scheduleActions || []).some(a =>
      a.actionType === 'sync_accounting'
      && (a.actionPayload as any)?.provider === provider
      && ((a.actionPayload as any)?.companyId ?? null) === companyId,
    );

  const loadSchedules = () =>
    scheduleService.getAll({ perPage: 100, page: 0 })
      .then(r => setSchedules(r.rows))
      .catch(() => setSchedules([]));

  useEffect(() => {
    if (open) {
      setCompanyId('');
      setScheduleId('');
      setErrors({});
      setAutoCommit(false);
      if (!isCloud) {
        thirdPartyConnectionDetailsService.getConnectedCompanies(provider)
          .then(r => {
            setCompanies(r || []);
            if (r && r.length === 1) setCompanyId(r[0].companyId);
          })
          .catch(() => setCompanies([]));
      }
      loadSchedules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, provider]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!isCloud && !companyId) errs.company = 'A company is required';
    if (!scheduleId) {
      errs.schedule = 'Select a schedule, or create a new one';
    } else {
      const selected = schedules.find(s => s.id === scheduleId);
      if (selected && scheduleHasThisSync(selected)) {
        errs.schedule = isCloud ? 'That schedule already syncs this provider' : 'That schedule already syncs this company';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await scheduleService.createAction(scheduleId, isCloud
        ? { actionType: 'sync_cloud_emissions', actionPayload: { provider: provider as CloudSyncProvider, autoCommit } }
        : { actionType: 'sync_accounting', actionPayload: { provider: provider as AccountingSyncProvider, companyId, companyName: selectedCompanyName } });
      const schedule = schedules.find(s => s.id === scheduleId);
      userNotificationsContext.openSnackbar({
        open: true,
        message: `${displayName} sync added to schedule "${schedule?.name ?? ''}"`,
        backgroundColor: 'primary.dark',
      });
      onClose();
    } catch (e: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: e?.response?.data?.message || 'An error occurred, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleCreated = async (newSchedule: ISchedule) => {
    setOpenCreateSchedule(false);
    await loadSchedules();
    setScheduleId(newSchedule.id);
    userNotificationsContext.openSnackbar({
      open: true,
      message: `Schedule "${newSchedule.name}" created`,
      backgroundColor: 'primary.dark',
    });
    onClose();
  };

  return (
    <>
      <DialogWrapper
        open={open}
        onClose={onClose}
        title={`Schedule ${displayName} sync`}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
        isSubmitting={isSubmitting}
        disableSaveButton={isSubmitting}
        maxWidth='sm'
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isCloud ? (
            <Typography variant='body2' color='text.secondary'>
              Automatically fetch monthly carbon figures from <strong>{displayName}</strong> on a schedule.
              Each run covers every open report period, and months needing a decision wait on this page.
              {isCloudEmissionsProvider(provider) ? ` ${CLOUD_PROVIDER_COPY[provider].scheduleTiming}` : ''}
            </Typography>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Automatically import transactions from <strong>{displayName}</strong> on a schedule. Each run
              imports into your open report period as outstanding records to review on the Financial page.
            </Typography>
          )}

          {isCloud && (
            <FormControlLabel
              control={<Switch checked={autoCommit} onChange={e => setAutoCommit(e.target.checked)} size='small' />}
              label={
                <Stack>
                  <Typography variant='body2'>Book months that need no decision</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Months with no invoice to pair are booked on their own, and invoice pairings are booked
                    once you have confirmed one by hand. Anything else waits for you.
                  </Typography>
                </Stack>
              }
            />
          )}

          {/* Company / tenant */}
          {!isCloud && <FormControl size='small' fullWidth error={!!errors.company}>
            <InputLabel>Company</InputLabel>
            <Select
              label='Company'
              value={companies.some(c => c.companyId === companyId) ? companyId : ''}
              onChange={e => {
                setCompanyId(e.target.value);
                if (errors.company) setErrors(prev => ({ ...prev, company: '' }));
              }}
            >
              {companies.length === 0 && <MenuItem disabled value=''>No connected companies</MenuItem>}
              {companies.map(c => (
                <MenuItem key={c.companyId} value={c.companyId}>{c.companyName}</MenuItem>
              ))}
            </Select>
            {errors.company && <FormHelperText>{errors.company}</FormHelperText>}
          </FormControl>}

          <Divider />

          {/* Add to schedule */}
          <Typography variant='subtitle2' fontWeight={600}>Schedule</Typography>

          <FormControl size='small' fullWidth error={!!errors.schedule}>
            <InputLabel>Add to schedule</InputLabel>
            <Select
              label='Add to schedule'
              value={schedules.some(s => s.id === scheduleId) ? scheduleId : ''}
              onChange={e => {
                setScheduleId(e.target.value);
                if (errors.schedule) setErrors(prev => ({ ...prev, schedule: '' }));
              }}
            >
              {schedules.length === 0 && <MenuItem disabled value=''>No schedules yet. Create one below</MenuItem>}
              {schedules.map(s => {
                const hasPendingRun = s.active && !!s.nextRunAt;
                const alreadySyncs = scheduleHasThisSync(s);
                return (
                  <MenuItem key={s.id} value={s.id} disabled={!hasPendingRun || alreadySyncs}>
                    {s.name}
                    {alreadySyncs ? (
                      <Typography variant='caption' color='text.disabled' sx={{ ml: 1 }}>
                        {isCloud ? 'Already syncs this provider' : 'Already syncs this company'}
                      </Typography>
                    ) : hasPendingRun ? (
                      <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                        (next: {new Date(s.nextRunAt!).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })})
                      </Typography>
                    ) : (
                      <Typography variant='caption' color='text.disabled' sx={{ ml: 1 }}>
                        No pending runs
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
            {errors.schedule && <FormHelperText>{errors.schedule}</FormHelperText>}
          </FormControl>

          {isAdmin && (
            <Box>
              <Button
                size='small'
                variant='text'
                startIcon={<AddOutlined />}
                onClick={() => setOpenCreateSchedule(true)}
                disabled={!isCloud && !companyId}
                sx={{ alignSelf: 'flex-start' }}
              >
                Create new schedule
              </Button>
              {!isCloud && !companyId && (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  Select a company first.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogWrapper>

      <ScheduleDialog
        open={openCreateSchedule}
        onClose={() => setOpenCreateSchedule(false)}
        successHandler={() => {}}
        defaultActions={isCloud
          ? [{ actionType: 'sync_cloud_emissions', provider, autoCommit }]
          : [{
            actionType: 'sync_accounting',
            provider,
            companyId,
            companyName: selectedCompanyName,
          }]}
        onCreated={handleScheduleCreated}
      />
    </>
  );
};
